/**
 * Impersonation utilities — DEV ONLY (gated server-side by DEV_MODE).
 *
 * Admin clicks "Test login as X" in /admin/coe/people. We POST to the
 * impersonate endpoint, then SWAP localStorage tokens:
 *
 *   ca_token            → the impersonated user's JWT
 *   ca_imp_backup_token → the admin's original JWT (so we can restore)
 *   ca_imp_backup_user  → JSON of admin's user, for the banner copy
 *   ca_imp_target_name  → display name of the impersonated user
 *
 * A sticky banner ('@/components/admin/ImpersonationBanner') shows up
 * across every portal as long as `ca_imp_backup_token` is set, and offers
 * an "Exit impersonation" button to restore the admin session.
 *
 * Same-tab (not new-tab) on purpose:
 *   - localStorage is shared between tabs of the same origin, so a new
 *     tab would clobber the admin's session anyway. Same-tab + banner
 *     is simpler, equally fast, and avoids any token plumbing tricks.
 */
import api from "@/lib/api";
import { toast } from "sonner";

const KEYS = {
    token: "ca_token",
    backupToken: "ca_imp_backup_token",
    backupUser: "ca_imp_backup_user",
    targetName: "ca_imp_target_name",
};

export function isImpersonating() {
    return !!localStorage.getItem(KEYS.backupToken);
}

export function getImpersonationTargetName() {
    return localStorage.getItem(KEYS.targetName) || "";
}

export function getBackupAdminName() {
    try {
        const raw = localStorage.getItem(KEYS.backupUser);
        if (!raw) return "";
        return (JSON.parse(raw) || {}).name || "";
    } catch {
        return "";
    }
}

/**
 * Kick off impersonation: POST /admin/coe/people/{id}/impersonate, swap
 * tokens, full-reload into the target portal.
 *
 * `currentUser` is the admin's own user object — we serialize it so the
 * "Exit impersonation" button can show "Return as {name}".
 */
export async function startImpersonation({ targetUserId, targetName, currentUser }) {
    try {
        const { data } = await api.post(`/admin/coe/people/${targetUserId}/impersonate`);
        const myToken = localStorage.getItem(KEYS.token);
        if (!myToken) {
            toast.error("Couldn't capture your admin session — refresh and try again.");
            return;
        }
        // Save backup BEFORE overwriting
        localStorage.setItem(KEYS.backupToken, myToken);
        localStorage.setItem(KEYS.backupUser, JSON.stringify(currentUser || {}));
        localStorage.setItem(KEYS.targetName, targetName || data.user?.name || "user");
        // Swap to the impersonation token
        localStorage.setItem(KEYS.token, data.token);
        // Full reload to the target's portal — AuthContext will re-fetch /auth/me
        toast.success(`Now signed in as ${data.user?.name || targetName}`);
        window.location.href = data.redirect_to || "/";
    } catch (e) {
        toast.error(e?.response?.data?.detail || "Failed to impersonate");
    }
}

/**
 * iter101.3 — Open the target portal in a NEW TAB, isolated by
 * sessionStorage. The admin's current tab keeps its localStorage token
 * untouched so both tabs stay usable side-by-side (perfect for testing
 * a doctor / patient view without losing the admin session).
 *
 * Flow (iter101.3a — popup-blocker safe + handshake-driven):
 *   1. Open the new tab SYNCHRONOUSLY within the click handler, pointing
 *      at `/impersonate-land?loading=1`. Popup blockers only fire when
 *      window.open runs from an async continuation (after await), so
 *      opening first is the standard workaround.
 *   2. POST /admin/coe/people/{id}/impersonate → { token, redirect_to }
 *   3. HANDSHAKE: child tab sends `{type: "ready"}` via
 *      `window.opener.postMessage` once its event listener is attached.
 *      This tab listens for that "ready" ping and replies with the
 *      token payload. Prevents the classic "listener not attached
 *      yet" race that plagues one-shot postMessage flows.
 *   4. Child writes token → sessionStorage → navigates to target portal.
 *
 * NOTE: sessionStorage is per-tab, so closing the new tab ends the
 * impersonated session automatically. We intentionally don't pass
 * "noopener" — the child needs window.opener to send the handshake.
 */
const MESSAGE_TYPE_READY = "invisique:impersonate:ready";
const MESSAGE_TYPE_TOKEN = "invisique:impersonate:token";

export async function openImpersonationInNewTab({ targetUserId, targetName }) {
    // 1. Open placeholder tab SYNCHRONOUSLY — defeats the popup blocker.
    const placeholder = "/impersonate-land?loading=1";
    let win = null;
    try { win = window.open(placeholder, "_blank"); } catch { /* handled below */ }
    if (!win) {
        toast.error("Pop-up blocked — allow pop-ups from this site so impersonated tabs can open.");
        return;
    }

    // 2. Fetch the impersonation token from the backend.
    let payload = null;
    let apiError = null;
    try {
        const { data } = await api.post(`/admin/coe/people/${targetUserId}/impersonate`);
        payload = {
            type: MESSAGE_TYPE_TOKEN,
            token: data.token,
            redirect: data.redirect_to || "/",
            label: targetName || data.user?.name || "user",
            role: data.user?.role || "",
        };
    } catch (e) {
        apiError = e?.response?.data?.detail || "Failed to impersonate";
    }

    // 3. Wait for the child's "ready" ping, then reply with the token.
    //    If the API call failed, reply with an error payload so the
    //    child tab shows something useful instead of a stuck spinner.
    const onReady = (ev) => {
        if (ev.origin !== window.location.origin) return;
        const d = ev.data || {};
        if (d.type !== MESSAGE_TYPE_READY) return;
        if (ev.source !== win) return;
        window.removeEventListener("message", onReady);
        if (payload) {
            ev.source.postMessage(payload, window.location.origin);
            toast.success(`Opened ${payload.label}'s portal in a new tab`);
        } else {
            ev.source.postMessage(
                { type: MESSAGE_TYPE_TOKEN, error: apiError || "Failed to impersonate" },
                window.location.origin,
            );
            toast.error(apiError || "Failed to impersonate");
        }
    };
    window.addEventListener("message", onReady);

    // 4. Safety net: if the child never pings within 15s, tear down
    //    the listener + close the popup so we don't leak either.
    setTimeout(() => {
        window.removeEventListener("message", onReady);
        // If the API failed AND the child never pinged, close the tab.
        if (apiError && win && !win.closed) {
            try { win.close(); } catch { /* noop */ }
        }
    }, 15000);
}

/**
 * Exit impersonation: restore the admin's token from backup, clear the
 * backup keys, and reload into /admin.
 */
export function exitImpersonation() {
    const backup = localStorage.getItem(KEYS.backupToken);
    if (!backup) {
        // Nothing to restore — just send back to /admin-login as a safety net
        localStorage.removeItem(KEYS.token);
        window.location.href = "/admin-login";
        return;
    }
    localStorage.setItem(KEYS.token, backup);
    localStorage.removeItem(KEYS.backupToken);
    localStorage.removeItem(KEYS.backupUser);
    localStorage.removeItem(KEYS.targetName);
    window.location.href = "/admin";
}
